import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [],
};

// Load from localStorage on init (safe)
if (typeof window !== "undefined") {
  try {
    const saved = localStorage.getItem("cart");
    if (saved) initialState.items = JSON.parse(saved);
  } catch (e) {
    console.error("Failed to load cart from localStorage:", e);
  }
}

const saveToLocalStorage = (items) => {
  try {
    localStorage.setItem("cart", JSON.stringify(items));
  } catch (e) {
    console.error("Failed to save cart to localStorage:", e);
  }
};

// Hourly service options - only one can be in cart at a time
const hourlyOptions = [
  "30 Min Express",
  "1 Hour Standard",
  "1.5 Hour Plus",
  "2 Hour Routine",
  "3 Hour Complete",
];

// Car wash options - max quantity of 3
const carWashOptions = [
  "Hatchback / Sedan",
  "SUV / MUV",
  "Luxury Car",
  "Truck / Bus",
];

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const option = action.payload;
      const existing = state.items.find((item) => item.id === option.id);
      const isHourly = hourlyOptions.includes(option.name);

      // Validation: Only block if trying to add a different hourly service
      if (!existing && isHourly) {
        // Check if cart already has a different hourly service
        const hasDifferentHourlyInCart = state.items.some(
          (item) => hourlyOptions.includes(item.name) && item.id !== option.id
        );

        // Block if trying to add hourly but cart already has a different hourly (only one hourly allowed)
        if (hasDifferentHourlyInCart) {
          console.warn(
            "Cannot add hourly service: cart already contains a different hourly service"
          );
          return; // Don't add the item
        }
      }

      if (existing) {
        // Increase quantity
        if (option.name === "Utensils" || option.name === "Kitchen Prep") {
          existing.quantity += option.addOn;
        } else {
          existing.quantity += 1;
        }

        // Always increase "unit" by 1
        existing.unit += 1;
      } else {
        state.items.push({
          ...option,
          offering: option.offering,

          quantity:
            option.name === "Utensils"
              ? option.unitCount
              : option.name === "Kitchen Prep"
              ? option.unitCount
              : 1,
          unit: 1, // 👈 new field, always starts from 1
        });
      }

      saveToLocalStorage(state.items);
    },

    removeFromCart(state, action) {
      const id = action.payload;
      state.items = state.items.filter((item) => item.id !== id);
      saveToLocalStorage(state.items);
    },

    increaseQuantity(state, action) {
      const id = action.payload;
      const existing = state.items.find((item) => item.id === id);

      if (existing) {
        // Check if car wash and already at max quantity (3)
        const isCarWash = carWashOptions.includes(existing.name);
        if (isCarWash && existing.quantity >= 3) {
          console.warn(
            "Cannot increase: max quantity of 3 reached for car wash"
          );
          return; // Don't increase
        }

        if (existing.name === "Utensils" || existing.name === "Kitchen Prep") {
          existing.quantity += existing.addOn;
        } else {
          existing.quantity += 1;
        }

        // increase unit
        existing.unit += 1;
      }

      saveToLocalStorage(state.items);
    },

    decreaseQuantity(state, action) {
      const id = action.payload;
      const existing = state.items.find((item) => item.id === id);

      if (existing) {
        if (existing.name === "Utensils") {
          if (existing.quantity > 20) {
            existing.quantity -= existing.addOn;
            existing.unit -= 1;
          } else {
            state.items = state.items.filter((item) => item.id !== id);
          }
        } else if (existing.name === "Kitchen Prep") {
          if (existing.quantity > 45) {
            existing.quantity -= existing.addOn;
            existing.unit -= 1;
          } else {
            state.items = state.items.filter((item) => item.id !== id);
          }
        } else {
          if (existing.quantity > 1) {
            existing.quantity -= 1;
            existing.unit -= 1;
          } else {
            state.items = state.items.filter((item) => item.id !== id);
          }
        }
      }

      saveToLocalStorage(state.items);
    },

    clearCart(state) {
      state.items = [];
      saveToLocalStorage(state.items);
    },
  },
});

// ✅ Selector to get total items count (units instead of raw qty)
export const getTotalItems = (state) =>
  state?.cart?.items?.reduce((total, item) => total + (item.unit ?? 0), 0) || 0;

export const {
  addToCart,
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;
